/* eslint-disable jsx-a11y/mouse-events-have-key-events */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import useWindowSize from '@hooks/use-window-size';
import vars from '@variables';
import styled from '@emotion/styled/macro';
import { PopUpTitle, PopUpDescription } from '@atoms/text-atoms/text-atoms';
import {
  InteractiveImageBlockProps,
  ModalDataProps,
} from './interactive-image-block.types';

const InteractiveImageBlock: React.FC<InteractiveImageBlockProps> = ({
  image,
  imageWidth,
  imageHeight,
  interactiveImagePinBlocks,
  className,
}) => {
  const [modal, setModal] = useState<ModalDataProps | false>(false);
  const windowWidth = useWindowSize().width;
  const showModal = windowWidth ? windowWidth > 480 : undefined;
  const [mousePos, setMousePos] = useState<{
    x: number | undefined;
    y: number | undefined;
  }>({ x: undefined, y: undefined });

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      setMousePos({
        x: event.pageX,
        y: event.pageY,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const onHover = pinDetails => {
    setModal(pinDetails);
  };

  const setXCoordinate = x => {
    if (windowWidth && windowWidth - 300 < x) {
      return x - 150;
    } else {
      return x;
    }
  };

  const renderTooltip = () => {
    if (!modal || !showModal) return;
    return (
      <div
        style={{
          left: `${setXCoordinate(mousePos?.x) || 0}px`,
          top: `${mousePos?.y ? mousePos?.y + 30 : 0}px`,
          position: 'absolute',
          padding: '1.2rem',
          pointerEvents: 'none',
          maxWidth: '25rem',
          backgroundColor: vars.greyHavens,
          boxShadow: '2px 2px 5px -2px rgba(16, 24, 40, 0.2)',
          borderRadius: '0.8rem',
          zIndex: vars.tooltipZindex,
        }}
        data-testid="tooltip"
      >
        <PopUpTitle>{modal.pinTitle}</PopUpTitle>
        {modal.pinDescription && (
          <PopUpDescription>
            {modal.pinDescription?.pinDescription}
          </PopUpDescription>
        )}
      </div>
    );
  };

  const renderPins = () => {
    return interactiveImagePinBlocks.map((pin, i) => {
      const {
        pinTitle,
        pinDescription,
        pinXCoordinate,
        pinYCoordinate,
        pinClickableWidth,
        pinClickableHeight,
        pinLinkUrl,
      } = pin;
      return (
        <a
          href={pinLinkUrl}
          onMouseOver={() => onHover({ pinTitle, pinDescription })}
          onMouseLeave={() => setModal(false)}
          data-testid={`pin-${i + 1}`}
          style={{ cursor: 'pointer' }}
          key={`pin-${i + 1}`}
        >
          <rect
            x={pinXCoordinate}
            y={pinYCoordinate}
            opacity="0"
            width={pinClickableWidth}
            height={pinClickableHeight}
          ></rect>
        </a>
      );
    });
  };

  return (
    <div className={className}>
      {renderTooltip()}
      <svg
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        viewBox={`0 0 ${imageWidth} ${imageHeight}`}
      >
        <image
          className="image"
          width={imageWidth}
          height={imageHeight}
          xlinkHref={image}
        ></image>
        {renderPins()}
      </svg>
    </div>
  );
};

const StyledInteractiveImageBlock = styled(InteractiveImageBlock)`
  margin-top: ${vars.spacingXxl};
  margin-bottom: ${vars.spacingXxl};
`;

export default StyledInteractiveImageBlock;
